import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import TermsAcceptBlock from "@/components/TermsAcceptBlock";
import { MobileOtpField } from "@/pages/Auth";

const CURRENT_TERMS_VERSION = "1.0";

/** Truthy when the user must still pass through the post-login gate.
 *  Two independent requirements: verified phone + current-version T&C. */
export function needsPostLoginGate(u) {
  if (!u) return false;
  // Admin / employee accounts bypass — they're internal.
  if (u.role === "admin" || u.role === "employee") return false;
  const noPhone = !u.phone_verified;
  const staleTerms = (u.terms_accepted_version || "") !== CURRENT_TERMS_VERSION;
  return noPhone || staleTerms;
}

/**
 * PostLoginGate — a full-screen, non-dismissible modal that captures the
 * two things every user must provide exactly once, regardless of whether
 * they signed up with email or Google:
 *
 *   1. Mobile number + OTP verification (Google users don't provide it at
 *      signup so they hit this gate on first login).
 *   2. Current-version Terms & Conditions + Privacy Policy acceptance.
 *
 * The gate renders as a portal above every route so the user cannot
 * dismiss it, back-button around it, or hit any listing/dashboard page
 * until both requirements are satisfied. Backend also enforces the same
 * gates on downstream endpoints as a defense-in-depth measure.
 */
export default function PostLoginGate() {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [otpProof, setOtpProof] = useState(null);
  const [acceptState, setAcceptState] = useState({ accepted: false, checkboxes: [] });
  const [submitting, setSubmitting] = useState(false);

  // Prefill phone from existing user record (Google users may already have
  // a phone if they finished this gate once and it was re-triggered by a
  // T&C version bump). We DO NOT prefill otpProof — verification must be
  // re-done any time this gate opens.
  useEffect(() => {
    if (user?.phone && !phone) setPhone(user.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phone]);

  if (!user) return null;
  if (!needsPostLoginGate(user)) return null;

  const needsPhone = !user.phone_verified;
  const termsRole = user.role === "both" ? "buyer" : user.role === "cam_vendor" ? "cam_vendor" : user.role;
  const OTP_ENABLED = Boolean(process.env.REACT_APP_MSG91_WIDGET_ID && process.env.REACT_APP_MSG91_WIDGET_TOKEN);

  const canSubmit = acceptState.accepted && (!needsPhone || !OTP_ENABLED || otpProof);

  const submit = async (e) => {
    e.preventDefault();
    if (needsPhone && OTP_ENABLED && !otpProof) {
      return toast.error("Please verify your mobile number with the OTP.");
    }
    if (!acceptState.accepted) {
      return toast.error("Please accept the Terms & Conditions and Privacy Policy.");
    }
    setSubmitting(true);
    try {
      const payload = {
        accept_terms: true,
        terms_version: CURRENT_TERMS_VERSION,
        checkboxes: acceptState.checkboxes,
      };
      if (needsPhone) {
        payload.phone = phone;
        payload.otp_proof_id = otpProof || undefined;
      }
      const { data } = await api.post("/auth/complete-profile", payload);
      setUser(data.user);
      toast.success("Welcome to Jeweltix ✓");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="post-login-gate"
    >
      <div className="bg-background border border-border rounded-lg shadow-2xl max-w-lg w-full my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-start gap-3 sticky top-0 bg-background z-10">
          <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display font-bold text-xl">Finish setting up your account</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              A one-time step so we can support you as a{" "}
              <span className="font-semibold text-foreground">{user.role === "cam_vendor" ? "CAM Vendor" : user.role}</span>.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          {needsPhone && (
            <section>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Step 1 — Mobile verification</p>
              <MobileOtpField phone={phone} setPhone={setPhone} otpProof={otpProof} setOtpProof={setOtpProof} />
            </section>
          )}

          <section>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
              Step {needsPhone ? "2" : "1"} — Terms &amp; Privacy
            </p>
            <TermsAcceptBlock role={termsRole} onChange={setAcceptState} />
          </section>

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="post-login-gate-submit"
            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-3 font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Continue to Jeweltix
          </button>
          <p className="text-[11px] text-muted-foreground text-center">
            You cannot use the platform until both steps are complete.
          </p>
        </form>
      </div>
    </div>,
    document.body,
  );
}
